import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { loginUser } from '@/api';
import { setToken } from '@/auth/token';
import GoogleButton from '@/pages/auth/GoogleButton';
import { postLoginPath } from '@/platform';
import ThemeToggle from '@/theme/ThemeToggle';

const ERRORS: Record<string, string> = {
  google_unavailable: 'Google sign-in is not configured on this server.',
  google_denied: 'Google sign-in was cancelled.',
  google_state: 'Google sign-in expired. Try again.',
  google_code: 'Google sign-in did not return a code.',
  google_exchange: 'Could not verify the Google account.',
  google_create: 'Could not create an account from Google.',
  google_token: 'Could not complete sign-in.',
  banned: 'This account has been banned.',
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || postLoginPath();
  const oauthErr = params.get('error');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const { token, user } = await loginUser(email, password);
      setToken(token, user);
      navigate(next.startsWith('/') ? next : postLoginPath());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'login failed');
    } finally {
      setBusy(false);
    }
  };

  const shown = err || (oauthErr ? ERRORS[oauthErr] || 'Google sign-in failed' : null);

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-bg text-textp px-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <form onSubmit={submit} className="w-[360px] bg-surface border border-border rounded-xl p-6 space-y-4">
        <Link to="/" className="text-lg font-semibold flex items-center gap-2">
          <i className="ri-stack-line text-accent" /> Sign in to Sourcetape
        </Link>
        {shown && <div className="text-sm text-error bg-error/10 border border-error/30 rounded-md px-3 py-2">{shown}</div>}
        <GoogleButton next={next} className="w-full" />
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-texts">
          <span className="flex-1 h-px bg-border" /> or <span className="flex-1 h-px bg-border" />
        </div>
        <label className="block text-xs text-texts">Email
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full bg-bg border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-accent" />
        </label>
        <label className="block text-xs text-texts">Password
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full bg-bg border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-accent" />
        </label>
        <button disabled={busy} className="w-full flex items-center justify-center gap-1.5 bg-accent hover:bg-accent-hover disabled:opacity-60 text-accent-text text-sm font-medium px-3 py-2 rounded-md">
          {busy ? <><i className="ri-loader-4-line animate-spin" /> Signing in</> : <><i className="ri-login-circle-line" /> Sign in</>}
        </button>
        <div className="text-xs text-texts text-center">No account? <Link to={`/register?next=${encodeURIComponent(next)}`} className="text-accent hover:underline">Register</Link></div>
        <p className="text-[11px] text-texts text-center leading-relaxed">
          By continuing you agree to the <Link to="/terms-of-use" className="text-accent hover:underline">Terms of Use</Link> and <Link to="/privacy" className="text-accent hover:underline">Privacy Policy</Link>.
        </p>
      </form>
    </div>
  );
}
